import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

function cls(...a){ return a.filter(Boolean).join(' '); }

function Pill({ children, tone='slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return <span className={cls('inline-flex items-center px-2 py-0.5 rounded-full border text-xs', map[tone])}>{children}</span>;
}

function Input({ label, value, onChange, type='text' }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <input
        type={type}
        value={value || ''}
        onChange={(e)=>onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
      />
    </label>
  );
}

function fmtDate(dt) { return dt ? String(dt).slice(0,10) : ''; }

// parse filename iz Content-Disposition ako postoji
function filenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  // npr: attachment; filename="billing_export_12.csv"
  const m = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(disposition);
  if (!m) return fallback;
  return decodeURIComponent(m[1].replace(/"/g, '').trim()) || fallback;
}

export default function AdminBillingExport() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [exporting, setExporting] = useState(false);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]).map((k) => Number(k)), [selected]);

  async function load() {
    try {
      setErr('');
      setLoading(true);
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const res = await API.get(`/billing/ready?${qs.toString()}`);
      const list = res.data || [];
      setRows(list);
      const nextSel = {};
      for (const r of list) nextSel[r.id] = true; // default select all
      setSelected(nextSel);
    } catch (e) {
      console.error(e);
      setErr('Failed to load claim-ready visits.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function toggleAll(on) {
    const next = {};
    for (const r of rows) next[r.id] = on;
    setSelected(next);
  }

  async function downloadCsv(exportId) {
    // ✅ token ide kroz Axios header (API instance)
    const res = await API.get(`/billing/exports/${exportId}/csv`, {
      responseType: 'blob',
    });

    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);

    const fallbackName = `billing_export_${exportId}.csv`;
    const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
    const filename = filenameFromDisposition(cd, fallbackName);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename; // ✅ “ponudi download”
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  }

  async function createExport() {
    try {
      setErr('');
      if (!selectedIds.length) return setErr('No visits selected.');
      setExporting(true);

      const res = await API.post('/billing/exports', { from: from || null, to: to || null, visit_ids: selectedIds });
      const exportId = res.data?.export_id;
      if (!exportId) throw new Error('No export_id');

      // ✅ download csv (sa tokenom)
      await downloadCsv(exportId);

      // reload list (exported visits should disappear)
      await load();
    } catch (e) {
      console.error(e);
      // ako je CSV endpoint vratio JSON error kao blob, ovde bi i dalje uletelo u catch
      setErr('Failed to create export batch or download CSV.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="p-3 md:p-6">
      <div className="rounded-3xl bg-gradient-to-br from-sky-50 via-white to-indigo-50 border border-slate-200 shadow-sm p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-lg md:text-xl font-semibold text-slate-900">Billing Export (Claim-ready)</div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={load} className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50">
              Refresh
            </button>
            <button
              disabled={exporting || !selectedIds.length}
              onClick={createExport}
              className={cls(
                'px-3 py-2 rounded-xl text-sm',
                exporting || !selectedIds.length ? 'bg-slate-200 text-slate-500' : 'bg-sky-600 text-white hover:bg-sky-700'
              )}
            >
              {exporting ? 'Exporting…' : `Create CSV Export (${selectedIds.length})`}
            </button>
          </div>
        </div>

        {err ? <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{err}</div> : null}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
          <Input label="From" type="date" value={from} onChange={setFrom} />
          <Input label="To" type="date" value={to} onChange={setTo} />
          <div className="flex items-end">
            <button onClick={load} className="w-full px-3 py-2 rounded-xl bg-sky-600 text-white text-sm hover:bg-sky-700">
              Apply
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Pill tone="sky">{rows.length} ready</Pill>
          <button onClick={() => toggleAll(true)} className="px-2.5 py-1.5 rounded-lg text-xs bg-white border border-slate-200 hover:bg-slate-50">Select all</button>
          <button onClick={() => toggleAll(false)} className="px-2.5 py-1.5 rounded-lg text-xs bg-white border border-slate-200 hover:bg-slate-50">Clear</button>
        </div>

        <div className="mt-4 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Sel</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Student</th>
                  <th className="px-3 py-2 text-left">Payer/Plan</th>
                  <th className="px-3 py-2 text-left">Member</th>
                  <th className="px-3 py-2 text-left">Auth</th>
                  <th className="px-3 py-2 text-left">CPT/POS</th>
                  <th className="px-3 py-2 text-left">Units</th>
                  <th className="px-3 py-2 text-left">EVV</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const studentName = `${r.student_last_name || ''}${r.student_last_name ? ', ' : ''}${r.student_first_name || ''}`.trim();
                  const mods = [r.modifier1, r.modifier2, r.modifier3, r.modifier4].filter(Boolean).join(',');
                  return (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={!!selected[r.id]}
                          onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                        />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-medium text-slate-900">{fmtDate(r.scheduled_at)}</div>
                        <div className="text-xs text-slate-600">visit #{r.id}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{studentName}</div>
                        <div className="text-xs text-slate-600">{r.teacher_name}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-900">{r.payer_name || '—'}</div>
                        <div className="text-xs text-slate-600">{r.plan_name || '—'}</div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{r.member_id || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.auth_number || '—'}</td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs">{r.cpt_code || '—'} {mods ? `(${mods})` : ''}</div>
                        <div className="font-mono text-xs text-slate-600">POS: {r.place_of_service || '—'}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs text-slate-900">
                          {r.unit_type === 'minute'
                            ? `${r.billed_minutes ?? ''} min`
                            : `${r.billed_units ?? ''} units`}
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.evv_required ? <Pill tone={r.evv_status === 'complete' ? 'emerald' : 'red'}>{r.evv_status}</Pill> : <Pill>not required</Pill>}
                      </td>
                    </tr>
                  );
                })}
                {!rows.length ? (
                  <tr>
                    <td className="px-3 py-6 text-slate-600" colSpan={9}>
                      {loading ? 'Loading…' : 'No claim-ready visits found (for this date range).'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


